import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './client.ts';

/**
 * Claves de caché en un solo sitio.
 *
 * Escribirlas a mano en cada pantalla es la forma habitual de que una invalidación no
 * llegue a donde tenía que llegar. Aquí, además, la jerarquía es la que hace que
 * `invalidateQueries({ queryKey: claves.liga(id) })` alcance también al detalle, a los
 * pronósticos y a la clasificación de esa liga.
 */
export const claves = {
  competiciones: () => ['competiciones'] as const,
  ligas: () => ['ligas'] as const,
  liga: (leagueId: string) => ['ligas', leagueId] as const,
  ligaDetalle: (leagueId: string) => ['ligas', leagueId, 'detalle'] as const,
  ligaPronosticos: (leagueId: string) => ['ligas', leagueId, 'pronosticos'] as const,
  ligaClasificacion: (leagueId: string) => ['ligas', leagueId, 'clasificacion'] as const,
  // La ventana entra en la clave: pedir 30 días no puede devolver el resumen de 7 que
  // ya estaba en caché.
  ligaRecap: (leagueId: string, days: number) => ['ligas', leagueId, 'recap', days] as const,
  pronosticosDelPartido: (leagueId: string, matchId: number) =>
    ['ligas', leagueId, 'partidos', matchId, 'pronosticos'] as const,
};

/**
 * Reintentar un 4xx no tiene sentido: la respuesta no va a cambiar por insistir, y en
 * un 404 de privacidad además retrasaría el estado que sí queremos enseñar. Solo se
 * reintentan los fallos de red y los 5xx.
 */
function debeReintentar(intento: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500) return false;
  return intento < 2;
}

export function crearQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: debeReintentar,
        // El calendario y los pronósticos cambian poco dentro de una sesión, pero
        // NADA de esto puede quedarse pegado: 30 s es suficiente para no repetir la
        // misma petición al navegar entre pantallas, y lo bastante corto para que un
        // resultado recién sincronizado aparezca solo.
        staleTime: 30_000,
        // Al volver a la pestaña se refresca: es una PWA que la gente deja abierta.
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Una mutación fallida NUNCA se reintenta sola: un pronóstico reenviado a
        // ciegas podría colarse justo después del kickoff.
        retry: false,
      },
    },
  });
}
